import { useState, type FormEvent } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";

const mono = { fontFamily: "ui-monospace, monospace" };

interface Filter {
  actor: string;
  action: string;
  target: string;
  outcome: string;
  from: string;
  to: string;
}

const NONE: Filter = { actor: "", action: "", target: "", outcome: "", from: "", to: "" };

/** The filter as query parameters, without empty fields; times are UTC. */
function query(f: Filter): Record<string, string> {
  const q: Record<string, string> = {};
  for (const [k, v] of Object.entries(f) as [keyof Filter, string][]) {
    if (v.trim()) q[k] = k === "from" || k === "to" ? `${v}:00Z` : v.trim();
  }
  return q;
}

export function AuditPage() {
  const [draft, setDraft] = useState<Filter>(NONE);
  const [filter, setFilter] = useState<Filter>(NONE);
  const set = (k: keyof Filter) => (e: { target: { value: string } }) =>
    setDraft({ ...draft, [k]: e.target.value });
  const search = (e: FormEvent) => {
    e.preventDefault();
    setFilter(draft);
  };
  const pages = useInfiniteQuery({
    queryKey: ["audit", filter],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/audit", {
          params: {
            query: {
              limit: 100,
              ...query(filter),
              ...(pageParam !== undefined && { before: pageParam }),
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.next_before ?? undefined,
  });
  const verify = useMutation({ mutationFn: () => unwrap(api.GET("/api/audit/verify")) });
  const entries = pages.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Audit log
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Button variant="outlined" onClick={() => verify.mutate()} disabled={verify.isPending}>
          Verify hash chain
        </Button>
        {verify.data &&
          (verify.data.valid ? (
            <Alert severity="success">Chain valid: {verify.data.entries_checked} entries checked</Alert>
          ) : (
            <Alert severity="error">Chain broken at entry {verify.data.first_invalid_seq}</Alert>
          ))}
        {verify.isError && <Alert severity="error">{verify.error.message}</Alert>}
      </Stack>
      <Paper variant="outlined" component="form" onSubmit={search} sx={{ p: 2 }} aria-label="Search the log">
        <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" label="Actor" value={draft.actor} onChange={set("actor")} />
          <TextField
            size="small"
            label="Action"
            helperText="Or its start, e.g. evidence."
            value={draft.action}
            onChange={set("action")}
          />
          <TextField
            size="small"
            label="Target"
            helperText="Part of it, e.g. a file's id"
            value={draft.target}
            onChange={set("target")}
          />
          <TextField
            select
            size="small"
            label="Outcome"
            value={draft.outcome}
            onChange={set("outcome")}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failure">Failure</MenuItem>
            <MenuItem value="denied">Denied</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="datetime-local"
            label="From (UTC)"
            value={draft.from}
            onChange={set("from")}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="Until (UTC)"
            value={draft.to}
            onChange={set("to")}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
          <Button
            onClick={() => {
              setDraft(NONE);
              setFilter(NONE);
            }}
          >
            Clear
          </Button>
          <Button variant="outlined" href={`/api/audit/export?${new URLSearchParams(query(filter)).toString()}`}>
            Export (signed)
          </Button>
        </Stack>
      </Paper>
      {pages.isError && <Alert severity="error">{pages.error.message}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Audit entries">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Time (UTC)</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.seq} selected={e.outcome !== "success"}>
                <TableCell>{e.seq}</TableCell>
                <TableCell sx={mono}>{e.t_utc.replace("T", " ").replace("Z", "")}</TableCell>
                <TableCell>{e.actor_name ?? "system"}</TableCell>
                <TableCell sx={mono}>{e.action}</TableCell>
                <TableCell>{e.target}</TableCell>
                <TableCell>{e.outcome}</TableCell>
                <TableCell sx={mono}>{e.ip ?? ""}</TableCell>
                <TableCell sx={{ ...mono, fontSize: 12 }}>{JSON.stringify(e.details)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pages.hasNextPage && (
        <div>
          <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
            Load older entries
          </Button>
        </div>
      )}
    </Stack>
  );
}
